/**
 * Suite auth — ProxyPro / VividLex plane only.
 * Same Google client, JWT, and wallet as Binder / BookForge / ImageForge / VideoForge / VoiceForge.
 *
 * Auth API:  https://proxy.vividlex.com
 * Debit client: agentforge
 */

import { loadSuiteSettings } from "./settings";
import type { SuiteUser } from "./types";

const SUITE_ACCESS = "forgesuite.auth.access";
const SUITE_REFRESH = "forgesuite.auth.refresh";
const BF_ACCESS = "bookforge.auth.access";
const BF_REFRESH = "bookforge.auth.refresh";
const CF_ACCESS = "forge_suite_access";
const CF_REFRESH = "forge_suite_refresh";

export const PROXYPRO_URL =
  (import.meta.env.VITE_PROXYPRO_URL as string | undefined)?.replace(/\/$/, "") ||
  "https://proxy.vividlex.com";

const CLIENT = "agentforge";

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: SuiteUser;
}

/** Always ProxyPro for identity — never bookforge-relay Google. */
export function authBaseUrl(): string {
  try {
    const s = loadSuiteSettings() as { authApiUrl?: string };
    const u = (s.authApiUrl || "").trim().replace(/\/$/, "");
    if (u && /proxy\.vividlex\.com/i.test(u)) return u;
  } catch {
    /* ignore */
  }
  return PROXYPRO_URL;
}

export function baseUrl(): string {
  return authBaseUrl();
}

export function getAccessToken(): string | null {
  try {
    return (
      localStorage.getItem(SUITE_ACCESS) ||
      localStorage.getItem(BF_ACCESS) ||
      localStorage.getItem(CF_ACCESS) ||
      null
    );
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  try {
    return (
      localStorage.getItem(SUITE_REFRESH) ||
      localStorage.getItem(BF_REFRESH) ||
      localStorage.getItem(CF_REFRESH) ||
      null
    );
  } catch {
    return null;
  }
}

export function isLoggedIn(): boolean {
  return Boolean(getAccessToken());
}

export function clearSession(): void {
  try {
    for (const k of [
      SUITE_ACCESS,
      SUITE_REFRESH,
      BF_ACCESS,
      BF_REFRESH,
      CF_ACCESS,
      CF_REFRESH,
      "agentforge.gateway.gsk",
      "agentforge.gateway.user",
    ]) {
      localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

export function saveTokens(access: string, refresh?: string): void {
  const a = (access || "").trim();
  if (a.length < 20 || a.length > 8192) throw new Error("Invalid access token");
  try {
    localStorage.setItem(SUITE_ACCESS, a);
    localStorage.setItem(BF_ACCESS, a);
    localStorage.setItem(CF_ACCESS, a);
    if (refresh) {
      const r = refresh.trim();
      if (r.length >= 20 && r.length <= 8192) {
        localStorage.setItem(SUITE_REFRESH, r);
        localStorage.setItem(BF_REFRESH, r);
        localStorage.setItem(CF_REFRESH, r);
      }
    }
  } catch {
    throw new Error("Browser storage is blocked; can't keep you signed in.");
  }
}

export async function api<T = unknown>(
  path: string,
  opts: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!headers.has("Content-Type") && opts.body) {
    headers.set("Content-Type", "application/json");
  }
  if (opts.auth !== false) {
    const tok = getAccessToken();
    if (tok) headers.set("Authorization", `Bearer ${tok}`);
  }
  let res: Response;
  try {
    res = await fetch(`${authBaseUrl()}${path}`, { ...opts, headers });
  } catch {
    throw new Error(
      "Can't reach suite auth (proxy.vividlex.com). Check network or try again.",
    );
  }
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    const raw = await res.text().catch(() => "");
    try {
      const j = JSON.parse(raw) as {
        error?: string;
        detail?: string | { error?: string };
        message?: string;
      };
      if (typeof j.detail === "object" && j.detail?.error) detail = j.detail.error;
      else detail = j.message || j.error || (typeof j.detail === "string" ? j.detail : detail);
    } catch {
      if (raw.trim()) detail = raw.length > 280 ? `${raw.slice(0, 280)}…` : raw;
    }
    if (res.status === 401) {
      clearSession();
      throw new Error("Session expired. Please sign in again.");
    }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function fetchMe(): Promise<SuiteUser & { wallet?: { balanceUsd?: number } }> {
  return api("/api/auth/me");
}

export async function googleStatus(): Promise<{
  enabled: boolean;
  redirect_uri: string;
  note?: string;
}> {
  return api("/api/auth/google/status", { auth: false });
}

export function startGoogleLoginWeb(): void {
  const origin = window.location.origin;
  const url = new URL(`${authBaseUrl()}/api/auth/google/start`);
  url.searchParams.set("return_origin", origin);
  url.searchParams.set("client", CLIENT);
  window.location.href = url.toString();
}

export function applyTokensFromHash(): boolean {
  try {
    const hash = window.location.hash || "";
    const path = window.location.pathname || "";
    if (
      !hash.includes("access_token") &&
      !hash.includes("token=") &&
      !path.includes("oauth")
    ) {
      return false;
    }

    const parts = hash.split("#").filter(Boolean);
    let params: URLSearchParams | null = null;
    for (const p of parts) {
      if (p.includes("access_token=") || p.includes("token=")) {
        const slice = p.startsWith("/") ? p.replace(/^\/?oauth\??/, "") : p;
        const idx = slice.indexOf("access_token=");
        const idx2 = slice.indexOf("token=");
        const start = idx >= 0 ? idx : idx2 >= 0 ? idx2 : slice.indexOf("=") >= 0 ? 0 : -1;
        params = new URLSearchParams(start >= 0 ? slice.slice(start) : slice);
        if (params.get("access_token") || params.get("token")) break;
      }
    }
    if (!params?.get("access_token") && !params?.get("token")) {
      const raw = hash.replace(/^#/, "");
      if (raw.includes("access_token=")) {
        params = new URLSearchParams(raw.slice(raw.indexOf("access_token=")));
      }
    }
    const access =
      params?.get("access_token") || params?.get("token") || params?.get("access");
    if (!access || access.length < 20) return false;
    saveTokens(access, params?.get("refresh_token") || params?.get("refresh") || undefined);
    const cleanPath =
      path.includes("oauth") || path.includes("account")
        ? "/"
        : window.location.pathname + (window.location.search || "");
    history.replaceState(null, "", cleanPath || "/");
    return true;
  } catch {
    return false;
  }
}

export async function probeRelay(): Promise<{ ok: boolean; message: string; service?: string }> {
  try {
    const res = await fetch(`${authBaseUrl()}/health`, { method: "GET" });
    if (!res.ok) return { ok: false, message: `Auth health HTTP ${res.status}` };
    const j = (await res.json().catch(() => ({}))) as { service?: string; status?: string };
    return {
      ok: true,
      message: j.service || j.status || "proxypro ok",
      service: j.service,
    };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) };
  }
}
